import { useEffect, useRef } from 'react'
import { useModal } from './ModalContext'

// Shared modal bodies so both the scroll narration (IntroText) and the LED ad ribbon
// (SponsorBoards) can open the same popups. Passed to openModal(<Component/>).

export function UpsideDownModal() {
    const { isPrewarm } = useModal()
    return (
        <div className="modal-doc">
            <h2>a recent game jam project</h2>
            <section>
                <h3>the upside down</h3>
                <div style={{ width: '100%', display: 'flex', justifyContent: 'center', background: 'transparent', borderRadius: 12, border: 'none', padding: 0 }}>
                    {!isPrewarm && (
                        <video
                            controls
                            playsInline
                            style={{ width: 360, maxWidth: '100%', height: 'auto', aspectRatio: '9 / 16', objectFit: 'cover', borderRadius: 10, display: 'block' }}
                            src="/Videos/the%20upside%20down%20game.mp4"
                            poster="/Videos/cover.jpg"
                        />
                    )}
                </div>
                <br /><br />
                <p>
                    this little game jam project flips control on its head: two characters, one input.
                    each step forward for one is a step backward for the other. the challenge? guide them both safely to their portals while dodging spikes, fire, and bottomless pits.
                    it starts simple, but quickly becomes the kind of brain-bending, frustratingly fun puzzle that makes you yell at the screen…
                    and then hit restart.
                </p>
            </section>
        </div>
    )
}

// focus="ux" scrolls straight to the UX section (used by the Figma/UX ad)
export function WhatIBringModal({ focus }) {
    const { isPrewarm } = useModal()
    const uxRef = useRef()
    useEffect(() => {
        if (focus === 'ux') {
            const t = setTimeout(() => uxRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 80)
            return () => clearTimeout(t)
        }
    }, [focus])
    return (
        <div className="modal-doc">
            <h2>What I Bring to the Table</h2>
            <section>
                <h3>Some Kickass Spreadsheets</h3>
                <img className="media" src={isPrewarm ? undefined : './Textures/combatsim.png'} alt="combat spreadsheet" onClick={() => window.open('https://docs.google.com/spreadsheets/d/17EEU5bTFlDaz8FBaP_6ujxTm6-vLe01aTKmlbMTGwR0/edit?gid=1444208808#gid=1444208808', '_blank', 'noopener,noreferrer')} style={{ cursor: 'pointer', outline: '3px solid black' }} />
                <p>
                    I have experience in systems design, progression, balancing, and economy design and thrive when building and testing in spreadsheets.
                    <br />    <br />
                    When we set out to fully develop War of Nova’s combat system, we knew we couldn’t just throw numbers together and hope for the best.
                    Strategy players are sharp — if the system is broken, they’ll find it. So we studied the giants in the 4X space, and one of the best examples was LOTR: Rise to War.
                    <br />    <br />
                    I broke down its entire combat system piece by piece and rebuilt it in Google Sheets.
                    The goal wasn’t just to copy their math, but to understand how their variables interacted and what lessons we could apply.
                    Through this interactive tool, we could simulate battles, tweak values, and stress-test edge cases.
                    We gained insight into balance curves, pacing, and player-facing clarity.
                    Studying games like this lets us learn from their lessons — both the good and the bad — and apply those insights to our own designs.
                    <br />    <br />
                    I rebuilt its combat system in Google Sheets to understand the underlying math, stress-test edge cases, and learn what makes combat feel fair yet exciting.
                </p>
                <div className="section-cta">
                    <button className="button-50" onClick={() => window.open('https://docs.google.com/spreadsheets/d/17EEU5bTFlDaz8FBaP_6ujxTm6-vLe01aTKmlbMTGwR0/edit?gid=1444208808#gid=1444208808', '_blank', 'noopener,noreferrer')}>open spreadsheet</button>
                </div>
            </section>
            <section>
                <h3>Narrative & Content Design</h3>
                <img className="media" src={isPrewarm ? undefined : './Textures/mafiascript.png'} alt="narrative doc" onClick={() => window.open('https://docs.google.com/document/d/1Nl_KExnxZXNZ2FGGYvnMMOfucMQA3gE7CBtCVYlcWL0/edit?tab=t.0', '_blank', 'noopener,noreferrer')} style={{ cursor: 'pointer', outline: '3px solid black' }} />
                <p>
                    I love storytelling: creating characters, writing narratives, and ideating content that breathes life into games.
                </p>
                <p>
                    For a mobile 4X game built around the Mafia franchise,
                    I designed a narrative-driven NUX that doubled as both a tutorial and an introduction to the world.
                    It introduced key characters, guided players through their first heist,
                    and taught the game’s core loop — all while staying true to the gritty charm of the Mafia brand.
                </p>
                <div className="section-cta">
                    <button className="button-50" onClick={() => window.open('https://docs.google.com/document/d/1Nl_KExnxZXNZ2FGGYvnMMOfucMQA3gE7CBtCVYlcWL0/edit?tab=t.0', '_blank', 'noopener,noreferrer')}>open doc</button>
                </div>
            </section>
            <section ref={uxRef}>
                <h3>UX Design</h3>
                <img className="media" src={isPrewarm ? undefined : './Textures/warofnovaux.png'} alt="spec doc" onClick={() => window.open('./Textures/Section 1.png', '_blank', 'noopener,noreferrer')} style={{ cursor: 'pointer', outline: '3px solid black' }} />
                <p>
                    I design UX flows and wireframes that make features intuitive and accessible, using tools like Figma and Photoshop to map player journeys.
                </p>
                <p>
                    Here’s a glimspe of the UX work I've done for War of Nova.
                </p>
                <div className="section-cta">
                    <button className="button-50" onClick={() => window.open('./Textures/Section 1.png', '_blank', 'noopener,noreferrer')}>view ux</button>
                </div>
            </section>
            <section>
                <h3>Documentation</h3>
                <img className="media" src={isPrewarm ? undefined : './Textures/spec.png'} alt="view ux" onClick={() => window.open('https://docs.google.com/document/d/1q1yxG0rqNZi7XnneEu_4tE-HspMgYKlfuc_oruOdBYQ/edit?tab=t.0', '_blank', 'noopener,noreferrer')} style={{ cursor: 'pointer', outline: '3px solid black' }} />
                <p>
                    I write clear, well-structured design documentation that don’t put you to sleep.
                </p>
                <p>
                    Here’s a sample version of one where I turned a messy feature idea into something readable, buildable, and iteration-ready.
                </p>
                <div className="section-cta">
                    <button className="button-50" onClick={() => window.open('https://docs.google.com/document/d/1q1yxG0rqNZi7XnneEu_4tE-HspMgYKlfuc_oruOdBYQ/edit?tab=t.0', '_blank', 'noopener,noreferrer')}>open doc</button>
                </div>
            </section>
        </div>
    )
}
